// The days an experience cannot be run at all: the artisan is away, or the whole
// thing turns on somewhere that is shut that day of the week.
//
// Closures live on the experience document rather than in a collection of their
// own, because they are a property of the experience in the way its price is —
// a handful of statements, edited on the one screen, read by everybody who reads
// the experience. The CMS's autosave, history and undo then cover them with
// nothing added, and the public page gets them from the document it already has.
//
// A closure is not the opposite of an availability slot. A slot is one artisan's
// agreed hour on a 'direct' experience; a closure is a day on which no request of
// any kind can be met, whatever the booking type. It matters most on an inquiry,
// which is the form where a concierge types a date nobody has checked yet — and
// where, until now, the only way to find out was to submit it and wait.
//
// Two kinds, because artisans describe their absences two ways. A holiday is a
// span of dates; "the market is shut on Mondays" is every Monday there will ever
// be, and writing that out as spans would be a list somebody has to keep topping
// up.

import { formatCalendarDate } from '@/util/util_format';

/** A span of dates the artisan is away for. Both ends inclusive. */
export interface ClosurePeriodType {
  /** `yyyy-mm-dd`. A single day off is the same date twice. */
  startDate: string;
  endDate: string;
  /**
   * Free text, shown to the concierge as-is. Optional: "away" is a complete
   * answer, and an admin typing the artisan's private reasons into a field the
   * partners read would be worse than a blank.
   */
  reason: string;
}

/** A day of the week the experience never runs on. */
export interface WeekdayClosureType {
  /** 0 = Sunday … 6 = Saturday, matching `Date.prototype.getDay`. */
  weekday: number;
  reason: string;
}

/**
 * The closure half of an experience, as the two fields it is stored in.
 *
 * A structural type rather than something `ExperienceType` extends: every caller
 * already holds a whole experience and passes it straight in, and naming the
 * shape here keeps the date logic below readable without a circular import.
 */
export interface ExperienceClosuresType {
  closurePeriods: ClosurePeriodType[];
  weekdayClosures: WeekdayClosureType[];
}

export const weekdayLabels = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const weekdayShortLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The order a week is read in here: Monday first, Sunday last. */
export const weekdayOrder = [1, 2, 3, 4, 5, 6, 0];

/** "Mondays" — the way a recurring closure is spoken about. */
export function weekdayPluralLabel(weekday: number): string {
  return `${weekdayLabels[weekday] ?? 'that day'}s`;
}

const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

/** Longest reason stored. Long enough for a sentence, short enough to show inline. */
export const closureReasonMaxLength = 120;

/**
 * Which day of the week a calendar date falls on, as 0–6.
 *
 * Parsed at noon UTC rather than midnight, for the same reason
 * `formatCalendarDate` does it: `yyyy-mm-dd` is a Japanese calendar day, not an
 * instant, and midnight UTC is the previous day for anybody west of Greenwich —
 * which would close the wrong weekday for half the world. Returns -1 for a date
 * that cannot be read, which matches nothing.
 */
export function isoDateWeekday(isoDate: string): number {
  if (!isoDatePattern.test(isoDate)) return -1;
  const parsed = new Date(`${isoDate}T12:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? -1 : parsed.getUTCDay();
}

/**
 * Which closure covers a date, or null when none does.
 *
 * A period beats a weekday when both apply, because it is the more specific
 * statement: "closed Mondays" is the standing arrangement, and a named absence
 * over the top of it is the thing worth telling the concierge about.
 *
 * Dates are compared as strings throughout. `yyyy-mm-dd` sorts as text exactly
 * as it does as a date, which is the reason that format is stored everywhere in
 * this codebase.
 */
export function findClosure(isoDate: string, closures: ExperienceClosuresType): ClosureMatchType | null {
  if (!isoDatePattern.test(isoDate)) return null;

  const period = closures.closurePeriods.find((p) => p.startDate <= isoDate && isoDate <= p.endDate);
  if (period) return { kind: 'period', period };

  const weekday = isoDateWeekday(isoDate);
  const weekly = closures.weekdayClosures.find((c) => c.weekday === weekday);
  return weekly ? { kind: 'weekday', closure: weekly } : null;
}

export type ClosureMatchType =
  | { kind: 'period'; period: ClosurePeriodType }
  | { kind: 'weekday'; closure: WeekdayClosureType };

export function isDateClosed(isoDate: string, closures: ExperienceClosuresType): boolean {
  return findClosure(isoDate, closures) !== null;
}

/**
 * An experience's own closures with a supplier's absences folded in.
 *
 * The supplier record holds the days the artisan is away once, rather than in
 * every experience they run (see types_supplier.ts). Everything that asks a
 * closure question — the date picker, the summary above it, the CMS calendar,
 * and `createBookingRequest` before it takes a booking — asks it of the merged
 * pair, so an absence typed on the supplier closes the day everywhere without
 * anything being copied onto the experience documents.
 *
 * Merged rather than replaced: an experience can still have absences of its own
 * that have nothing to do with the artisan's diary, and linking a supplier must
 * not quietly drop them.
 *
 * The experience's own periods come first, so where both cover a date it is the
 * experience's reason a concierge is shown — the more specific statement, on the
 * same principle that makes a period beat a weekday in `findClosure`. Returns
 * the original object when there is nothing to add, which keeps the reference
 * stable for the memoised callers.
 */
export function withInheritedClosures(
  closures: ExperienceClosuresType,
  inherited: ClosurePeriodType[],
): ExperienceClosuresType {
  if (inherited.length === 0) return closures;
  return {
    closurePeriods: [...closures.closurePeriods, ...inherited],
    weekdayClosures: closures.weekdayClosures,
  };
}

/** Whether anything at all is configured. Nothing shown to a concierge when not. */
export function hasClosures(closures: ExperienceClosuresType): boolean {
  return closures.closurePeriods.length > 0 || closures.weekdayClosures.length > 0;
}

/** The reason, or an empty string. Both kinds carry one and neither has to. */
export function closureReason(match: ClosureMatchType): string {
  return match.kind === 'period' ? match.period.reason : match.closure.reason;
}

/**
 * Why a date cannot be asked for, as one sentence a concierge can act on.
 *
 * Shared by the form's own check and by `validateBookingRequest`, so the line
 * that appears under a date input is the same one the server would refuse the
 * submission with.
 */
export function closureMessageFor(isoDate: string, closures: ExperienceClosuresType): string | null {
  const match = findClosure(isoDate, closures);
  if (!match) return null;
  const reason = closureReason(match);
  const because = reason ? ` (${reason})` : '';
  return match.kind === 'weekday'
    ? `This experience does not run on ${weekdayPluralLabel(match.closure.weekday)}${because}.`
    : `The artisan is not available on ${formatCalendarDate(isoDate)}${because}.`;
}

/** "12 Aug 2026 – 20 Aug 2026", or the single date when the span is one day. */
export function closurePeriodDatesLabel(period: Pick<ClosurePeriodType, 'startDate' | 'endDate'>): string {
  return period.startDate === period.endDate
    ? formatCalendarDate(period.startDate)
    : `${formatCalendarDate(period.startDate)} – ${formatCalendarDate(period.endDate)}`;
}

/** One closure as a line: "Closed every Monday — market closed". */
export function describeClosure(match: ClosureMatchType): string {
  const reason = closureReason(match);
  const head = match.kind === 'weekday'
    ? `Closed every ${weekdayLabels[match.closure.weekday] ?? 'week'}`
    : `Unavailable ${closurePeriodDatesLabel(match.period)}`;
  return reason ? `${head} — ${reason}` : head;
}

/**
 * The standing weekly closures, as lines to show a concierge.
 *
 * Separate from the absences below because the two are read differently. "Closed
 * every Monday" is a rule about the experience that no calendar can state — a
 * concierge scanning for a date needs to know it before they start looking, and
 * it never stops being true. A fortnight in August is a fact about a particular
 * fortnight, which the picker shows in place, struck out on the days themselves.
 */
export function weekdayClosureLines(closures: ExperienceClosuresType): string[] {
  return sortWeekdayClosures(closures.weekdayClosures)
    .map((closure) => describeClosure({ kind: 'weekday', closure }));
}

/**
 * The dated absences still worth mentioning, as lines to show a concierge.
 *
 * Periods that have already ended are dropped: last summer's holiday explains
 * nothing about a date anybody can still book, and a list that only grows is one
 * nobody reads. Kept in the stored data, though — deleting it is the admin's
 * call, and the record of why a past request was refused is worth having.
 */
export function closurePeriodLines(closures: ExperienceClosuresType, today: string): string[] {
  return sortClosurePeriods(closures.closurePeriods)
    .filter((period) => period.endDate >= today)
    .map((period) => describeClosure({ kind: 'period', period }));
}

/**
 * Every closure still worth mentioning, weekly rules first.
 *
 * For the places that show the whole picture at once — the calendar popover,
 * where the list sits under the grid the same days are struck out on. The
 * booking card above it lists only the weekly half; see `weekdayClosureLines`.
 */
export function closureSummaryLines(closures: ExperienceClosuresType, today: string): string[] {
  return [...weekdayClosureLines(closures), ...closurePeriodLines(closures, today)];
}

/** Calendar order. Start date, then end date for two absences beginning together. */
export function compareClosurePeriods(a: ClosurePeriodType, b: ClosurePeriodType): number {
  return a.startDate === b.startDate ? a.endDate.localeCompare(b.endDate) : a.startDate.localeCompare(b.startDate);
}

export function sortClosurePeriods(periods: ClosurePeriodType[]): ClosurePeriodType[] {
  return [...periods].sort(compareClosurePeriods);
}

/** Monday first, the way a week is read on a roster. */
export function sortWeekdayClosures(closures: WeekdayClosureType[]): WeekdayClosureType[] {
  return [...closures].sort((a, b) => weekdayOrder.indexOf(a.weekday) - weekdayOrder.indexOf(b.weekday));
}

/**
 * Why a closure cannot be added, or null when it can.
 *
 * Overlapping absences are allowed on purpose — an artisan away for a week
 * inside a month the workshop is shut is two true statements, and `findClosure`
 * simply reports the first. What is refused is a span that cannot be read, one
 * that ends before it starts, and one already listed exactly.
 */
export function validateClosurePeriod(
  draft: ClosurePeriodType,
  existing: ClosurePeriodType[],
): string | null {
  if (!isoDatePattern.test(draft.startDate) || !isoDatePattern.test(draft.endDate)) {
    return 'Please give both dates.';
  }
  if (draft.endDate < draft.startDate) return 'The last day cannot be before the first.';
  if (draft.reason.length > closureReasonMaxLength) {
    return `Please keep the reason under ${closureReasonMaxLength} characters.`;
  }
  if (existing.some((p) => p.startDate === draft.startDate && p.endDate === draft.endDate)) {
    return 'Those dates are already listed as closed.';
  }
  return null;
}
